/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-no-useless-fragment */
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import GlobalLoader from '@efex-components/common/GlobalLoader/GlobalLoader';
import { formatFullName } from '@utils/formater';
import useIsLogged from '../../hooks/useIsLogged';
import { fetchUserProfile } from '../../pages/profile/store';
import {
  selectProfileData,
  selectProfileState,
} from '../../pages/profile/store/selectors';
import mixpanelSvc from '../../services/mixpanel/mixpanelSvc';
import { useAppDispatch } from '../../store';
import { fetchWallets } from '../wallets/store';
import { fetchCombinedBalance } from '../combined_balance/store';

interface AuthInitializerProps {
  children: React.ReactNode;
}

const AuthInitializer = ({ children }: AuthInitializerProps) => {
  const dispatch = useAppDispatch();
  const isUserLogged = useIsLogged();
  const location = useLocation();
  const userData = useSelector(selectProfileData);
  const userProfile = useSelector(selectProfileState);
  const { update } = useIntercom();

  const { isUserInactive, acceptedTerms, hasTransactionalProfile } =
    userData || {};

  const enterpriseId = useMemo(() => {
    if (userData?.company) return userData?.company?.enterpriseId;
    return null;
  }, [userData]);

  useEffect(() => {
    if (!userData?.isTestUser) {
      mixpanelSvc.track(`Navigate to ${location.pathname}`);
    }
  }, [location.pathname, userData]);

  useEffect(() => {
    if (isUserLogged && !userProfile.data) {
      dispatch(fetchUserProfile());

      if (
        userData?.legalRepresentative &&
        userData?.company &&
        !userData.isTestUser
      ) {
        mixpanelSvc.people.set({
          $email: userData.legalRepresentative.email,
          $name: userData.legalRepresentative.name,
          $last_name: userData.legalRepresentative.lastName,
          $country: userData.company.country,
        });
      }
    }
    if (isUserLogged && userData?.naturalPerson) {
      const { naturalPerson, intercomHMAC, uuid } = userData || {};
      const { email, name, lastName } = naturalPerson || {};
      if (email && name && lastName && intercomHMAC) {
        const fullName = formatFullName(name, lastName);
        update({
          email,
          name: fullName,
          userId: uuid,
          userHash: intercomHMAC,
        });
      }
    }
  }, [isUserLogged, userProfile.data]);

  useEffect(() => {
    if (location.pathname === '/dashboard' && enterpriseId) {
      dispatch(fetchWallets(enterpriseId));
      dispatch(fetchCombinedBalance(enterpriseId));
    }
  }, [enterpriseId, location.pathname]);

  if (!isUserLogged) return <Navigate to="/login" />;

  if (userProfile.loading || !userProfile.data) {
    return <GlobalLoader />;
  }
  if (isUserInactive && location.pathname !== '/inactive') {
    return <Navigate to="inactive" />;
  }
  if (
    hasTransactionalProfile &&
    !acceptedTerms &&
    !isUserInactive &&
    location.pathname !== '/terms-and-conditions'
  ) {
    return <Navigate to="terms-and-conditions" />;
  }

  return <>{children}</>;
};

export default AuthInitializer;
